import type { JobResponse } from '@siteday/shared';
import { Plus } from 'lucide-react';
import { useMemo, useState } from 'react';
import { Link } from 'react-router';

import { errorMessage } from '@/api/client';
import { ErrorState, FilterBar, LoadingState, PageHeader } from '@/components/PageHeader';
import { JobStatusPill } from '@/components/StatusPill';
import { Pill } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Dialog } from '@/components/ui/dialog';
import { Input, Select } from '@/components/ui/input';
import { Card, EmptyRow, Table, Th } from '@/components/ui/table';
import { useToast } from '@/components/ui/toast';
import { fmtDateMedium } from '@/lib/dates';
import { sortRows, useTableSort } from '@/lib/sort';
import { useUrlState } from '@/lib/url-state';

import { JobForm, toJobCreate, useForemen, useQboCandidates, useQboStatus } from './JobForm';
import { ReconcileDialog } from './ReconcileDialog';
import { useCreateJob, useJobsList } from './useJobs';

const DEFAULTS = { status: 'active', q: '', foreman: '', sort: '' };

/** W5 — jobs table, status filter (default active), search, New job, Reconcile for field-created jobs. */
export function JobsPage() {
  const [state, setState] = useUrlState(DEFAULTS);
  const jobs = useJobsList({ status: state.status === 'all' ? undefined : state.status });
  const foremen = useForemen();
  const qbo = useQboStatus();
  const candidates = useQboCandidates(qbo.data?.connected === true);
  const sort = useTableSort(state.sort, (v) => setState({ sort: v }));
  const [creating, setCreating] = useState(false);
  const [reconcile, setReconcile] = useState<JobResponse | null>(null);

  const rows = useMemo(() => {
    const q = state.q.trim().toLowerCase();
    const list = (jobs.data?.items ?? [])
      .filter(
        (j) =>
          !q || j.name.toLowerCase().includes(q) || (j.clientName ?? '').toLowerCase().includes(q),
      )
      .filter((j) => !state.foreman || j.foremen.some((f) => f.id === state.foreman));
    return sortRows(list, sort.column, sort.dir, {
      job: (j) => j.name,
      client: (j) => j.clientName ?? '',
      foremen: (j) => j.foremen.map((f) => f.name).join(', '),
      status: (j) => j.status,
      start: (j) => j.startDate ?? '',
    });
  }, [jobs.data, state.q, state.foreman, sort.column, sort.dir]);

  const customerName = (id: string | null) =>
    id ? (candidates.data?.customers.find((c) => c.id === id)?.name ?? id) : null;

  return (
    <>
      <PageHeader
        title="Jobs"
        actions={
          <Button variant="primary" onClick={() => setCreating(true)}>
            <Plus /> New job
          </Button>
        }
      />
      <FilterBar>
        <Input
          type="search"
          aria-label="Search jobs"
          placeholder="Search jobs or clients…"
          value={state.q}
          onChange={(e) => setState({ q: e.target.value })}
          className="w-56"
        />
        <Select
          aria-label="Status"
          value={state.status}
          onChange={(e) => setState({ status: e.target.value })}
          className="w-auto"
        >
          <option value="active">Status · Active</option>
          <option value="bidding">Status · Bidding</option>
          <option value="complete">Status · Complete</option>
          <option value="all">Status · All</option>
        </Select>
        <Select
          aria-label="Foreman"
          value={state.foreman}
          onChange={(e) => setState({ foreman: e.target.value })}
          className="w-auto"
        >
          <option value="">Foreman · All</option>
          {foremen.data?.items.map((f) => (
            <option key={f.id} value={f.id}>
              {f.name}
            </option>
          ))}
        </Select>
      </FilterBar>

      {jobs.isPending ? (
        <Card>
          <LoadingState />
        </Card>
      ) : jobs.isError ? (
        <ErrorState error={jobs.error} onRetry={() => void jobs.refetch()} />
      ) : (
        <Card>
          <Table>
            <thead>
              <tr>
                <Th sortKey="job" sort={sort}>
                  Job
                </Th>
                <Th sortKey="client" sort={sort}>
                  Client
                </Th>
                <Th sortKey="foremen" sort={sort}>
                  Foremen
                </Th>
                <Th sortKey="status" sort={sort}>
                  Status
                </Th>
                <Th sortKey="start" sort={sort}>
                  Start
                </Th>
                <Th>QuickBooks customer</Th>
                <Th />
              </tr>
            </thead>
            <tbody>
              {rows.length === 0 ? (
                <EmptyRow colSpan={7}>
                  {state.q ? 'No jobs match your search.' : 'No jobs yet. Create the first one.'}
                </EmptyRow>
              ) : null}
              {rows.map((j) => (
                <tr key={j.id} className={j.createdByForeman ? 'hi' : undefined}>
                  <td>
                    <Link
                      to={`/jobs/${j.id}`}
                      className="font-semibold text-orange-ink hover:underline"
                    >
                      {j.name}
                    </Link>
                    <span className="sub">{j.siteAddress ?? 'No address yet'}</span>
                    {j.createdByForeman ? (
                      <Pill tone="orange" className="mt-1">
                        Created in the field
                      </Pill>
                    ) : null}
                  </td>
                  <td>{j.clientName ?? '—'}</td>
                  <td>{j.foremen.map((f) => f.name).join(', ') || '—'}</td>
                  <td>
                    <JobStatusPill status={j.status} />
                  </td>
                  <td className="num">{fmtDateMedium(j.startDate)}</td>
                  <td className={j.qboCustomerId ? undefined : 'text-ink3'}>
                    {customerName(j.qboCustomerId) ?? 'not mapped'}
                  </td>
                  <td className="act">
                    {j.createdByForeman ? (
                      <Button size="sm" variant="primary" onClick={() => setReconcile(j)}>
                        Reconcile
                      </Button>
                    ) : null}
                    <Link to={`/jobs/${j.id}`}>
                      <Button size="sm">Open</Button>
                    </Link>
                  </td>
                </tr>
              ))}
            </tbody>
          </Table>
        </Card>
      )}

      <NewJobDialog open={creating} onClose={() => setCreating(false)} />
      {reconcile ? (
        <ReconcileDialog job={reconcile} open onClose={() => setReconcile(null)} />
      ) : null}
    </>
  );
}

function NewJobDialog({ open, onClose }: { open: boolean; onClose: () => void }) {
  const create = useCreateJob();
  const toast = useToast();
  return (
    <Dialog open={open} onClose={onClose} title="New job" width="lg">
      {open ? (
        <JobForm
          submitLabel="Create job"
          pending={create.isPending}
          onCancel={onClose}
          onSubmit={(v) =>
            create.mutate(toJobCreate(v), {
              onSuccess: (job) => {
                toast.success(`${job.name} created`);
                onClose();
              },
              onError: (e) => toast.error(errorMessage(e)),
            })
          }
        />
      ) : null}
    </Dialog>
  );
}
